// Google, Discord and Steam on the player web (F-IAM-03 … F-IAM-05, spec/07 §2, §3.1, §3.4): the buttons on sign-in
// and sign-up, linking one from a signed-in session, and reading what a refused round trip comes back with.
//
//   Offered only where it works: `GET /v1/meta`'s `signIn.social` names the providers this deployment has switched
//   on (`useSocialProviders`), and a provider that is off has no button at all.
//
//   Every URL is on this portal's own origin. The portal reaches the api through `/v1` on its own origin, and the
//   server decides the provider's redirect URI; the portal only says where the browser lands afterwards
//   (`callbackURL`) and where a refusal lands (`errorCallbackURL`, with `?error=<code>` added). A `provider=` of our
//   own rides on the refusal's URL so the screen can name the provider; nothing secret is ever in a URL.
//
//   A provider button goes straight to the provider (owner, 2026-10-08): no terms first. An existing player never
//   sees the terms. A provider account with no Clavian account yet comes back `?error=CONSENT_REQUIRED`, and the
//   screen opens "Create your Clavian account" (`CreateAccountDialog`) with the terms and privacy; agreeing starts
//   the same provider's round trip again, this time with the consent, and the account is created on its way back.
//   Starting again, rather than holding the provider's answer on the server until the consent arrives, keeps
//   nothing about an unconsented person anywhere and needs no new state; the provider remembers the player, so the
//   second trip is one click (spec/07 §3.1). A new account then lands on "Complete your profile" (`/welcome`).
//
//   Linking from a session: `/v1/auth/link-social` for Google and Discord, `/v1/auth/steam/start` with `link: true`
//   for Steam. No sign-in ever joins an existing account by email (`account_not_linked`): the player signs in, then
//   links from the account page.

import type { ApiClient, SignUpConsent, SocialProvider } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, Dialog, SocialButton, SocialIconButton } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { useProblem } from '../api/use-problem';
import { useBrandLegal, type BrandLegal } from '../api/queries';

/** Leaving for the provider: the browser's own navigation, replaceable in a test (jsdom navigates nowhere). */
export const providerNavigation = { assign: (url: string): void => window.location.assign(url) };

/** A path of this portal as an absolute URL on its own origin: what the server's trusted origins accept. */
export const here = (path: string): string => new URL(path, window.location.origin).toString();

/** `path` with `params` added to its query, keeping what it had. */
export function withQuery(path: string, params: Readonly<Record<string, string | undefined>>): string {
  const url = new URL(path, 'http://portal.invalid');
  for (const [key, value] of Object.entries(params)) if (value !== undefined) url.searchParams.set(key, value);
  return `${url.pathname}${url.search}`;
}

export const isSocialProvider = (value: unknown): value is SocialProvider =>
  value === 'google' || value === 'discord' || value === 'steam';

export interface SocialRoundTrip {
  /** Where the browser lands when it worked: a path of this portal. */
  readonly landing: string;
  /** Where it lands refused, with `?error=` added: a path of this portal. */
  readonly onError: string;
  /** The consent a new account is created with (sign-up), or nothing (sign-in, linking). */
  readonly consent?: SignUpConsent;
  /** Link the provider to the signed-in account rather than sign in. */
  readonly link?: boolean;
  /** Where an account this round trip creates lands instead of `landing`: "Complete your profile". */
  readonly newUserLanding?: string;
}

/** Ask the api for the provider's URL. Steam has its own endpoint; a link has its own for Google and Discord. */
export async function providerUrl(api: ApiClient, provider: SocialProvider, trip: SocialRoundTrip): Promise<string> {
  const callbackURL = here(trip.landing);
  const errorCallbackURL = here(withQuery(trip.onError, { provider }));
  const newUser = trip.link === true || trip.newUserLanding === undefined ? {} : { newUserCallbackURL: here(trip.newUserLanding) };
  if (provider === 'steam') {
    const answer = await api.steamStart({
      body: {
        callbackURL,
        errorCallbackURL,
        ...newUser,
        ...(trip.link === true ? { link: true } : {}),
        ...(trip.consent === undefined ? {} : { consent: trip.consent }),
      },
    });
    return answer.url;
  }
  if (trip.link === true) {
    const answer = await api.linkSocialAccount({ body: { provider, callbackURL, errorCallbackURL, disableRedirect: true } });
    return answer.url;
  }
  const answer = await api.socialSignIn({
    body: {
      provider,
      callbackURL,
      errorCallbackURL,
      ...newUser,
      disableRedirect: true,
      ...(trip.consent === undefined ? {} : { consent: trip.consent }),
    },
  });
  if (answer.url === undefined) throw new Error('No provider URL came back.');
  return answer.url;
}

/** What a refused round trip's `?error=` means, as the last segment of a copy key (`social.error.*`). */
export type SocialRefusal =
  | 'noAccount'
  | 'notLinked'
  | 'cancelled'
  | 'expired'
  | 'banned'
  | 'refused'
  | 'takenByOther'
  | 'unverified'
  | 'generic';

export function socialRefusal(code: string | undefined): SocialRefusal | undefined {
  switch (code) {
    case undefined:
    case '':
      return undefined;
    case 'CONSENT_REQUIRED':
      return 'noAccount';
    case 'account_not_linked':
    case 'OAUTH_LINK_ERROR':
      return 'notLinked';
    case 'access_denied':
      return 'cancelled';
    case 'state_mismatch':
    case 'state_not_found':
    case 'state_security_mismatch':
    case 'please_restart_the_process':
    case 'invalid_callback_request':
    case 'no_code':
      return 'expired';
    case 'BANNED_USER':
      return 'banned';
    case 'SIGN_UP_REFUSED':
      return 'refused';
    case 'account_already_linked_to_different_user':
      return 'takenByOther';
    case 'unable_to_link_account':
    case 'email_not_verified':
      return 'unverified';
    default:
      return 'generic';
  }
}

export interface SocialButtonsProps {
  readonly providers: readonly SocialProvider[];
  /** The round trip each button starts; `null` stops it. */
  readonly trip: (provider: SocialProvider) => SocialRoundTrip | null | Promise<SocialRoundTrip | null>;
  /** Words on the button and its name: "Continue with Google" unless given. */
  readonly label?: (provider: SocialProvider) => string;
}

/** Start a provider's round trip: ask the api for its URL and leave for it. */
export function useStartSocial() {
  const api = useApi();
  const [starting, setStarting] = useState<SocialProvider | null>(null);
  const problem = useProblem();
  const [failed, setFailed] = useState<SocialProvider | null>(null);
  // What the server said, when it said something specific (a rate limit, a sign-up that is off): else the caller's own line.
  const [reason, setReason] = useState<string | undefined>(undefined);
  const start = async (provider: SocialProvider, round: SocialRoundTrip) => {
    setFailed(null);
    setReason(undefined);
    setStarting(provider);
    try {
      providerNavigation.assign(await providerUrl(api, provider, round));
    } catch (error) {
      const view = problem.view(error);
      setReason(view.specific ? problem.text(error) : undefined);
      setFailed(provider);
      setStarting(null);
    }
  };
  return { start, starting, failed, reason };
}

/**
 * One button per provider that is on, in the providers' own colours; nothing when none is. One provider is the
 * full-width "Continue with Google"; two or three stand in a row as their marks alone, each named and with a
 * tooltip (as the console and the kiosk draw them).
 */
export function SocialButtons({ providers, trip, label }: SocialButtonsProps): ReactNode {
  const { t } = useTranslation('player');
  const { start, starting, failed, reason } = useStartSocial();
  if (providers.length === 0) return null;

  const press = async (provider: SocialProvider) => {
    const round = await trip(provider);
    if (round !== null) await start(provider, round);
  };
  const words = (provider: SocialProvider) => label?.(provider) ?? t(`social.continue.${provider}`);

  return (
    <div className="pw-social">
      {providers.length === 1 ? (
        providers.map((provider) => (
          <SocialButton key={provider} provider={provider} busy={starting === provider} onClick={() => void press(provider)}>
            {words(provider)}
          </SocialButton>
        ))
      ) : (
        <div className="clav-social-row" role="group" aria-label={t('social.groupLabel')}>
          {providers.map((provider) => (
            <SocialIconButton key={provider} provider={provider} label={words(provider)} busy={starting === provider} onClick={() => void press(provider)} />
          ))}
        </div>
      )}
      {failed === null ? null : (
        <p className="pw-alert" role="alert">
          {reason ?? t('social.start', { provider: t(`social.provider.${failed}`) })}
        </p>
      )}
    </div>
  );
}

/** Where the two links stand in the consent sentence: private-use characters no translation contains. */
const TERMS_MARK = '\uE000';
const PRIVACY_MARK = '\uE001';

/** "I agree to the Terms of Service and the Privacy Policy", with the two documents in force linked inside it. */
export function ConsentLabel({ legal }: { readonly legal: BrandLegal }): ReactNode {
  const { t } = useTranslation('player');
  const template = t('signUp.consent.labelLinked', { terms: TERMS_MARK, privacy: PRIVACY_MARK });
  return template.split(new RegExp(`(${TERMS_MARK}|${PRIVACY_MARK})`)).map((part, index) =>
    part === TERMS_MARK ? (
      <a key={index} href={legal.terms.url} target="_blank" rel="noopener noreferrer">
        {t('signUp.consent.terms')}
      </a>
    ) : part === PRIVACY_MARK ? (
      <a key={index} href={legal.privacy.url} target="_blank" rel="noopener noreferrer">
        {t('signUp.consent.privacy')}
      </a>
    ) : (
      part
    ),
  );
}

export interface CreateAccountDialogProps {
  /** The provider whose round trip came back `CONSENT_REQUIRED`; `null` while the dialog is shut. */
  readonly provider: SocialProvider | null;
  /** The round trip to start again, with the consent: the same landing and refusal page as the first. */
  readonly trip: (provider: SocialProvider) => SocialRoundTrip;
  readonly onClose: () => void;
}

/**
 * "Create your Clavian account": the terms and privacy in force, agreed in a centred dialog, then the provider's round
 * trip again with them. Shown only when a provider account has no Clavian account yet (`CONSENT_REQUIRED`).
 */
export function CreateAccountDialog({ provider, trip, onClose }: CreateAccountDialogProps): ReactNode {
  const { t } = useTranslation('player');
  const { legal } = useBrandLegal();
  const { start, starting, failed, reason } = useStartSocial();
  const [agreed, setAgreed] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);
  const name = provider === null ? '' : t(`social.provider.${provider}`);

  const go = async () => {
    if (provider === null || legal === undefined) return;
    if (!agreed) {
      setNeedsConsent(true);
      return;
    }
    const consent: SignUpConsent = { terms: legal.terms.version, privacy: legal.privacy.version, ...(marketing ? { marketing: true } : {}) };
    await start(provider, { ...trip(provider), consent });
  };

  return (
    <Dialog
      title={t('signUp.dialog.title')}
      description={t('signUp.dialog.body', { provider: name })}
      open={provider !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('signUp.dialog.cancel')}
          </Button>
          <Button variant="primary" busy={provider !== null && starting === provider} disabled={legal === undefined} onClick={() => void go()}>
            {t('signUp.dialog.confirm', { provider: name })}
          </Button>
        </>
      }
    >
      {legal === undefined ? (
        <p className="pw-alert" role="status">
          {t('signUp.legalUnavailable')}
        </p>
      ) : (
        <div className="pw-form">
          <Checkbox
            label={<ConsentLabel legal={legal} />}
            checked={agreed}
            onCheckedChange={(checked) => {
              setAgreed(checked === true);
              if (checked === true) setNeedsConsent(false);
            }}
            aria-invalid={needsConsent}
          />
          {needsConsent ? (
            <p className="pw-alert" role="alert">
              {t('signUp.consent.required')}
            </p>
          ) : null}
          <Checkbox label={t('signUp.consent.marketing')} checked={marketing} onCheckedChange={(checked) => setMarketing(checked === true)} />
          {failed === null ? null : (
            <p className="pw-alert" role="alert">
              {reason ?? t('social.start', { provider: name })}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** "or", between the password form and the providers. */
export function SocialDivider(): ReactNode {
  const { t } = useTranslation('player');
  return (
    <p className="pw-social__or" aria-hidden="true">
      <span>{t('social.or')}</span>
    </p>
  );
}
